import { Component, HttpService } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { TwService } from '../modules/tailwind/services'
import type { ShopifyTheme } from '../types/index.js'
import type { FormSubmitResponse } from '../types/index.js'
import { jsonStringify } from '@ribajs/utils/src/index.js'
import { SbLocaleService } from '../services'
import { GoogleRecaptchaService } from '../services/google-recaptcha.service.js'
import { WindowGoogleRecaptcha } from '../types/google-recaptcha.js'

interface InputContact {
  /** Value of the email input */
  email: string
  /** Value of the phone input */
  phone: string
  /** Value of the fax input */
  fax: string
  /** Value of the companyWebsite input */
  companyWebsite: string
  /** Value of the subject input */
  subject: string
  /** Value of the matter select */
  matter: string
  /** Value of the matter supplier specification select */
  matterSupplierFor: string
  /** Value of the firstName input */
  title: string
  /** Value of the firstName input */
  firstName: string
  /** Value of the lastName input */
  lastName: string
  /** Value of the streetNumber input */
  streetNumber: string
  /** Value of the zip code input */
  zipcode: string
  /** Value of the city input */
  city: string
}

interface InputComplaint {
  /** Value of the batchCode input */
  batchCode: string
  /** Value of the productName input */
  productName: string
  /** Value of the bestBefore input */
  bestBefore: string
  /** Value of the productionCode input */
  productionCode: string
  /** Value of the productSize input */
  productSize: string
  /** Value of the purchaseLocation input */
  purchaseLocation: string
  /** Value of the purchaseStillAvailable radio input */
  purchaseStillAvailable: boolean | string
  /** Value of the reason textarea */
  reason: string
}

interface InputOther {
  /** Value of the language input */
  language: string
  /** Value of the privacy checkbox */
  privacy: boolean
  /** Value of the recaptchaResponse input */
  recaptchaResponse?: string | null
}

type Input = InputContact & Partial<InputComplaint> & InputOther

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  configUrl: string
  submitUrl: string
  environment: ShopifyTheme['role']
  type: 'contact' | 'complaint'
  language: string
  showCompanyWebsite: (matter: string) => boolean

  feedback: {
    success: boolean
    successMessage: string
    error: boolean
    errorMessage: string
  }
  showForm: boolean
  loading: boolean

  // INPUTS
  input: Input

  // METHODS
  /** Called when the form is submitted */
  onSubmit: SbContactComponent['onSubmit']
  /** Call this method to submit the form manually */
  submit: SbContactComponent['submit']
  /** Called when an input event is triggered */
  inputEventHandler: SbContactComponent['inputEventHandler']

  // TEMPLATES

  // Elements
  formEl?: HTMLFormElement
  attachmentEl?: HTMLInputElement
}

interface EnvironmentServiceUrls {
  staging: {
    contact: string
    complaint: string
  }
  production: {
    contact: string
    complaint: string
  }
}

/**
 * A simple template for new component, just make a copy of this template, adjust the class / tag name and add a new export to `src/riba/components/index.ts`.
 */
export class SbContactComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-template foobar="hello">...</sb-template>`
   */
  public static tagName = 'sb-contact-form'

  protected localeService = SbLocaleService.getSingleton()
  public static configServiceUrls: EnvironmentServiceUrls = {
    production: {
      contact: 'https://shop-services.seeberger.de/v1/contact-config',
      complaint: 'https://shop-services.seeberger.de/v1/complaint-config',
    },
    staging: {
      contact:
        'https://seeberger-shop-services-staging.pluspol-networks.de/v1/contact-config',
      complaint:
        'https://seeberger-shop-services-staging.pluspol-networks.de/v1/complaint-config',
    },
  }

  public static submitServiceUrls: EnvironmentServiceUrls = {
    production: {
      contact: 'https://shop-services.seeberger.de/v1/contact',
      complaint: 'https://shop-services.seeberger.de/v1/complaint',
    },
    staging: {
      contact:
        'https://seeberger-shop-services-staging.pluspol-networks.de/v1/contact',
      complaint:
        'https://seeberger-shop-services-staging.pluspol-networks.de/v1/complaint',
    },
  }
  protected tailwind = TwService.getSingleton()

  public scope: Scope = {
    // OPTIONS
    configUrl: '',
    submitUrl: '',
    environment: 'main',
    type: 'contact',
    language: 'de',
    showCompanyWebsite: this.showCompanyWebsite,

    input: {
      // CONTACT INPUTS
      email: '',
      phone: '',
      fax: '',
      companyWebsite: '',
      subject: '',
      matter: '',
      matterSupplierFor: '',
      title: '',
      firstName: '',
      lastName: '',
      streetNumber: '',
      zipcode: '',
      city: '',

      // COMPLAINT INPUTS
      batchCode: '',
      productName: '',
      bestBefore: '',
      productionCode: '',
      productSize: '',
      purchaseLocation: '',
      reason: '',
      purchaseStillAvailable: '',

      // OTHER INPUTS
      language: 'de',
      privacy: true,
      recaptchaResponse: 'UNSAFE',
    },

    feedback: {
      success: false,
      successMessage: '',
      error: false,
      errorMessage: '',
    },
    showForm: true,
    loading: false,
    // METHODS
    onSubmit: this.onSubmit,
    submit: this.submit,
    inputEventHandler: this.inputEventHandler,

    // TEMPLATES
  }

  private recaptcha!: Promise<WindowGoogleRecaptcha>
  private recaptchaWidgetId: number | undefined

  static get observedAttributes(): string[] {
    return ['config-url', 'submit-url', 'environment', 'type', 'language']
  }

  protected requiredAttributes(): string[] {
    return ['environment', 'type']
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbContactComponent.observedAttributes)
  }

  async beforeBind() {
    await super.beforeBind()
    const environment =
      this.scope.environment === 'main' ? 'production' : 'staging'
    if (!this.scope.configUrl) {
      this.scope.configUrl =
        SbContactComponent.configServiceUrls[environment][this.scope.type]
    }

    if (!this.scope.submitUrl) {
      this.scope.submitUrl =
        SbContactComponent.submitServiceUrls[environment][this.scope.type]
    }
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbContactComponent.tagName}-ready`)
    this.scope.input.language = this.scope.language
    this.generateForm(await this.getConfig())
  }

  public async getConfig() {
    const config = await HttpService.getJSON(this.scope.configUrl)
    return config
  }

  protected async generateForm(config: any) {
    if (!this) {
      console.error('Form container not found')
      return
    }
    const formContainer = this.scope.formEl

    this.recaptcha = GoogleRecaptchaService.load()

    // Add the reCAPTCHA field at the end of the form
    const recaptchaContainer = document.createElement('div')
    formContainer?.querySelector('.captcha')?.appendChild(recaptchaContainer)
    this.recaptcha.then(recaptcha => {
      this.recaptchaWidgetId = recaptcha.render(recaptchaContainer, {
        sitekey: config.body.recaptchaSiteKey,
      })
    })
  }

  protected submitForm() {
    // You can perform any additional validation or processing here before submitting

    // Submit the form using your preferred method (e.g., AJAX, form submit)
    // For example, using AJAX with the Fetch API:
    const formData = new FormData(this.scope.formEl)
    fetch(this.scope.submitUrl, {
      method: 'PUT',
      body: jsonStringify(formData),
    })
      .then(response => response.json())
      .then(data => {
        console.log('Form submission successful:', data)
        // You can handle the response as needed
      })
      .catch(error => {
        console.error('Form submission error:', error)
        // Handle errors here
      })
  }

  /**
   * Submit the form by hand by calling this method
   */
  public async submit() {
    if (!this.scope.formEl) {
      throw new Error('formEl is not defined')
    }
    try {
      // this.scope.formEl.submit()
      this.scope.formEl.dispatchEvent(new Event('submit', { cancelable: true }))
    } catch (error) {
      console.error(error)
    }

    return new Promise<FormSubmitResponse>((resolve, reject) => {
      this.addEventListener(
        'contact-form-submit:done',
        event => {
          const formResult = (event as CustomEvent<FormSubmitResponse | null>)
            .detail
          if (formResult) {
            resolve(formResult)
          } else {
            reject(formResult)
          }
        },
        { once: true },
      )
    })
  }

  /** Called on submit, see `rv-on-submit="onSubmit"` in the template */
  public async onSubmit(event: Event) {
    // Inline event prevent default
    event?.preventDefault()

    // Simplify invalid form handling
    const isValid = this.scope.formEl?.checkValidity()
    if (!isValid) {
      return true
    }

    this.scope.loading = true

    try {
      let response: Response
      switch (this.scope.type) {
        case 'complaint':
          response = await this.sendComplaintRequest(
            this.prepareComplaintData(),
          )
          break
        case 'contact':
          response = await this.sendContactRequest(
            this.prepareContactData() as any,
          )
          break
      }

      const result = await this.processResponse(response)

      // Simplify loading and form feedback
      this.scope.loading = false

      if (result.success) {
        this.handleSuccess(result)
      } else {
        this.handleError(result)
      }

      // reset recaptcha element
      ;(await this.recaptcha).reset(this.recaptchaWidgetId)

      // Dispatch event here with result object
      this.dispatchEvent(
        new CustomEvent('contact-form-submit:done', { detail: result }),
      )
    } catch (error) {
      console.error(error)
      this.scope.loading = false
      this.scope.feedback.error = true
      this.scope.feedback.errorMessage = (error as Error)?.message || 'Error'

      // Dispatch event here with null as no valid response given
      this.dispatchEvent(
        new CustomEvent('contact-form-submit:done', { detail: null }),
      )
    }
  }

  private getReCaptchaResponse() {
    const captchaEl = this.scope.formEl?.querySelector(
      '[name="g-recaptcha-response"]',
    ) as HTMLInputElement | null

    return captchaEl?.value
  }

  private prepareContactData() {
    return {
      ...this.scope.input,

      // For suppliers there's a second field to specify the matter
      matter:
        this.scope.input.matter === 'supplierFor'
          ? this.scope.input.matterSupplierFor
          : this.scope.input.matter,
      matterSupplierFor: undefined,

      recaptchaResponse: this.getReCaptchaResponse(),
      source: 'shop',
    }
  }

  private prepareComplaintData(): FormData {
    // Copy input scope
    const data: Input & { source: string } = {
      ...this.scope.input,
      // set purchaseStillAvailable to true if the value is 'true' or true and to false if the value is 'false' or false and to n/a if the value is something else
      purchaseStillAvailable:
        this.scope.input.purchaseStillAvailable === 'true' ||
        this.scope.input.purchaseStillAvailable === true,
      recaptchaResponse: this.getReCaptchaResponse(),
      bestBefore: this.scope.input.bestBefore || undefined,
      source: 'shop',
    }

    // Transform to formData
    const formData = new FormData()
    formData.append('data', JSON.stringify(data))

    // Add attachment
    if (this.scope.attachmentEl?.files?.length) {
      formData.append('attachment', this.scope.attachmentEl.files[0])
    }
    return formData
  }

  private showCompanyWebsite(matter: string): boolean {
    const showCompanyWebsite = [
      'general',
      'businessTrade',
      'sponsoring',
      'supplierFor',
    ].includes(matter)
    return showCompanyWebsite
  }

  private async sendComplaintRequest(data: FormData): Promise<Response> {
    const response = await fetch(this.scope.submitUrl, {
      method: 'PUT',
      body: data,
    })
    return response
  }

  private async sendContactRequest(data: any): Promise<Response> {
    const response = await fetch(this.scope.submitUrl, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'content-type': 'application/json',
      },
    })
    return response
  }

  private async processResponse(response: Response) {
    const result = {
      status: response.status,
      success: false,
      error: false,
      message: '',
      errorMessage: '',
    }
    const i18nErrorMessage = await this.localeService._(
      'general.forms.response.error.text',
    )

    const i18nSuccessMessage = await this.localeService._(
      'general.forms.response.success.text',
      {
        firstname: this.scope.input.firstName,
        subject: this.scope.input.productName || this.scope.input.subject,
      },
    )

    // Here, make sure to get the status appropriately
    if (response.status === 201) {
      result.success = true
      result.message = i18nSuccessMessage
    } else {
      const body = await response.json()
      if (body.errors) {
        const errorFields = this.processErrorItems(body.errors)
        result.error = true
        result.errorMessage = `${i18nErrorMessage} ${errorFields}` || 'Error'
      }
    }

    return result
  }

  private processErrorItems(errors: { field: string }[]): string {
    const errorLabels = errors
      .map(errorItem => this.processErrorItem(errorItem))
      .filter(Boolean)

    return errorLabels.join(', ')
  }

  private processErrorItem(errorItem: { field: string }): string | null {
    const fieldName = errorItem.field.split('.').pop()
    if (!fieldName) return null
    const errorLabelElement = document.querySelector(
      `label[for="${fieldName}"]`,
    )
    const errorElement = document.getElementById(`${fieldName}-error`)

    if (!errorLabelElement || !errorElement) {
      console.error(`Unable to find elements for field: ${fieldName}`)
      return null
    }

    this.displayError(errorElement)
    this.scrollToFirstError()
    return errorLabelElement.textContent?.trim().replace(/\s*\*$/, '') || null
  }

  private displayError(errorElement: Element): void {
    errorElement.closest('.group')?.classList.toggle('has-error', true)
  }

  private clearFieldError(fieldId: string): void {
    const errorElement = document.getElementById(`${fieldId}-error`)

    if (errorElement) {
      this.hideError(errorElement)
    } else {
      console.error(`Unable to find element for field: ${fieldId}`)
    }
  }

  private scrollToSuccessMessage(): void {
    const successMessageElement = this.querySelector('.success-message')

    if (successMessageElement) {
      successMessageElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  private scrollToFirstError(): void {
    const firstErrorElement = this.querySelector('.has-error')

    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  private hideError(errorElement: Element): void {
    errorElement.parentElement?.classList.toggle('has-error', false)
  }

  protected inputEventHandler(event: Event): void {
    const isValid = (event.target as HTMLInputElement).validity.valid
    const fieldId = (event.target as HTMLInputElement).id
    if (isValid) {
      this.clearFieldError(fieldId)

      if (fieldId === 'matterSupplierFor') {
        // Workaround for: If the user submits the form with matter "supplierFor" but doesn't specify this in "matterSupplierFor"
        // the field "matter" will be marked as invalid. Therefore when selecting a value in "matterSupplierFor" it's necessary to reset "matter" too
        this.clearFieldError('matter')
      }
    }
  }

  private handleSuccess(result: { message?: any }): void {
    this.scope.showForm = false

    this.scope.feedback.success = true
    this.scope.feedback.error = false
    this.scope.feedback.successMessage = result.message
    this.scope.feedback.errorMessage = ''
    this.scrollToSuccessMessage()
  }

  private handleError(result: { errorMessage?: any }): void {
    this.scope.feedback.error = true
    this.scope.feedback.success = false
    this.scope.feedback.errorMessage = result.errorMessage
    this.scope.feedback.successMessage = ''
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected async template() {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return <p>Error: A child html template is required</p>
    }
    return null
  }
}
